import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Stack } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useEffect } from 'react';
import { Platform } from 'react-native';
import 'react-native-reanimated';
// Import each weight from its own subpath, NOT from the package root. The
// package index require()s every weight it ships (9 upright + 9 italic each),
// so a root import drags ~40 unused .ttf files into the bundle — several MB of
// app size for fonts that are never rendered.
import { useFonts } from 'expo-font';
import { Inter_400Regular } from '@expo-google-fonts/inter/400Regular';
import { Inter_500Medium } from '@expo-google-fonts/inter/500Medium';
import { Inter_600SemiBold } from '@expo-google-fonts/inter/600SemiBold';
import { Inter_700Bold } from '@expo-google-fonts/inter/700Bold';
import { Fraunces_600SemiBold } from '@expo-google-fonts/fraunces/600SemiBold';
import { Fraunces_700Bold } from '@expo-google-fonts/fraunces/700Bold';

import { useColorScheme } from '@/hooks/use-color-scheme';

export const unstable_settings = {
  anchor: '(tabs)',
};

// Keep the splash on screen longer (and fade it out) instead of the default
// blink. We hold it manually, then hide after a short branded beat.
// NATIVE ONLY — this API controls the native launch screen and has no
// business running on web. This same root layout also serves the standalone
// web content pages (privacy.tsx/terms.tsx/support.tsx); running the
// splash-hide logic there caused the exported page to flash its real content
// then go blank white, because expo-splash-screen's web shim left a full-
// screen overlay in an inconsistent state after hydration.
if (Platform.OS !== 'web') {
  SplashScreen.preventAutoHideAsync().catch(() => {});
  SplashScreen.setOptions({ duration: 600, fade: true });
}

export default function RootLayout() {
  const colorScheme = useColorScheme();
  // Type system: Fraunces (soft display serif) for headers/display moments,
  // Inter for all body/UI text — Inter carries the small stuff (task copy,
  // scores, XP, percentiles) where legibility on the dark background matters
  // most. Loaded via expo-font at runtime — JS/asset only, so it works in the
  // existing dev build with no native rebuild.
  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    Fraunces_600SemiBold,
    Fraunces_700Bold,
  });

  useEffect(() => {
    if (Platform.OS === 'web') return;
    // Hold the splash until the fonts are ready so text never flashes in the
    // system font first — with a safety cap so a slow/failed font load can't
    // wedge the splash on screen forever.
    if (fontsLoaded) {
      SplashScreen.hideAsync().catch(() => {});
      return;
    }
    const t = setTimeout(() => {
      SplashScreen.hideAsync().catch(() => {});
    }, 3000);
    return () => clearTimeout(t);
  }, [fontsLoaded]);

  return (
    <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <Stack>
        <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
        <Stack.Screen name="modal" options={{ presentation: 'modal', title: 'Modal' }} />
        <Stack.Screen name="privacy" options={{ headerShown: false }} />
        <Stack.Screen name="terms" options={{ headerShown: false }} />
        <Stack.Screen name="support" options={{ headerShown: false }} />
      </Stack>
      <StatusBar style="auto" />
    </ThemeProvider>
  );
}
